import React, { useState, useEffect, useCallback } from "react";

import { useTranslation } from "react-i18next";
import ErrorBanner from "../components/ui/ErrorBanner";
import SkeletonList from "../components/ui/SkeletonList";
import { IoMdArrowBack } from "react-icons/io";
import { IoClose } from "react-icons/io5";

import { useNavigate } from "react-router-dom";
import { useLoading } from "../context/LoadingContext";
import { useAuth } from "../context/AuthContext";
import { fetchReferralEarned, fetchReferralLink, fetchReferralRebateList, fetchReferralCommissionList } from "../utils/api";
import config from '../config';
import { formatCurrency } from "../utils/global";
import FloatingMenu from "../components/FloatingMenu";

const Referral = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoading();
    const navigate = useNavigate();
    const { token } = useAuth();
    const isLoggedIn = Boolean(token);

    const [referralLoading, setReferralLoading] = useState(isLoggedIn);
    const [referralList, setReferralList] = useState([]);
    const [referral, setReferral] = useState("");
    const [referralRebateList, setReferralRebateList] = useState([]);
    const [totalCommission, setTotalCommission] = useState(0);
    const [totalReferrals, setTotalReferrals] = useState(0);

    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (!isLoggedIn) {
            setLoading(false);
            setReferralLoading(false);
            setErrorMessage("");
            setReferralList([]);
            setReferral("");
            setReferralRebateList([]);
            setTotalCommission(0);
            setTotalReferrals(0);
            return;
        }

        const loadData = async () => {
            setLoading(true);
            setReferralLoading(true);
            setErrorMessage("");

            try {
                const [referralResponse, userReferralResponse, rebateResponse, referralCommissionResponse] = await Promise.all([
                    fetchReferralEarned(),
                    fetchReferralLink(),
                    fetchReferralRebateList(),
                    fetchReferralCommissionList()
                ]);
                if (referralResponse.success && Array.isArray(referralResponse.data) && referralResponse.data.length > 0) {
                    setReferralList(referralResponse.data);
                } else {
                    setReferralList([]);
                }

                if (userReferralResponse.success) {
                    setReferral(userReferralResponse.data);
                } else {
                    setReferral("");
                    setErrorMessage(userReferralResponse.data || t("failedToFetchLink"));
                }

                if (rebateResponse.success && Array.isArray(rebateResponse.data)) {
                    setReferralRebateList(rebateResponse.data);
                } else {
                    setReferralRebateList([]);
                }

                if (referralCommissionResponse.success) {
                    const commission = referralCommissionResponse.total_cashback || 0;
                    setTotalCommission(commission);
                    setTotalReferrals(referralCommissionResponse.referrer_count || 0);
                } else {
                    setTotalCommission(0);
                    setTotalReferrals(0);
                }
            } catch (error) {
                setErrorMessage(t("pleaseTryLater"));
            } finally {
                setLoading(false);
                setReferralLoading(false);
            }
        };

        loadData();
    }, [isLoggedIn, token, t, setLoading]);

    useEffect(() => {
        if (errorMessage) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [errorMessage]);

    const fallbackCopyTextToClipboard = useCallback((text) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();

        try {
            document.execCommand("copy");
            alert(t("idCopied"));
        } catch (err) {
            console.error("Fallback copy failed:", err);
        }

        document.body.removeChild(textArea);
    }, [t]);

    const handleCopyId = useCallback((id) => {
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard
                .writeText(id)
                .then(() => alert(t("idCopied")))
                .catch((err) => {
                    console.error("Clipboard API failed, using fallback:", err);
                    fallbackCopyTextToClipboard(id);
                });
        } else {
            fallbackCopyTextToClipboard(id);
        }
    }, [t, fallbackCopyTextToClipboard]);

    return (
        <section className="page-shell">
            <div className="page-header">
                <div
                    className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                    onClick={() => navigate("/")}
                >
                    <IoMdArrowBack className="size-sm" />
                    <span className="text-sm">
                        {t("backToLobby")}
                    </span>
                </div>
                <button className="cursor-pointer" onClick={() => navigate("/")}>
                    <IoClose className="size-lg text-color-blue" />
                </button>
            </div>

            <div className="page-content">
                <ErrorBanner message={errorMessage} />

                <div className="pb-3 lg:bg-transparent lg:px-0 lg:pt-2 lg:pb-6">
                    {referralLoading ? (
                        <SkeletonList rows={1} className="rounded-lg lg:rounded-[2rem]" />
                    ) : (
                        <>
                        <div className="dropdown-gradient-border-second mb-4 flex-center flex-col px-[14px] py-5 rounded-lg">
                            <span className="text-lg text-white font-bold">
                                {t("inviteNewUserToGet")}
                            </span>
                            <span className="text-sm text-white font-medium text-center my-3">
                                {t("referralDescription")}
                            </span>

                            {isLoggedIn && referral && (
                                <>
                                    <div
                                        className="w-full flex items-center rounded-lg bg-secondary container-equal-sm text-white px-4 cursor-pointer mb-3"
                                        onClick={() => referral && handleCopyId(referral)}
                                    >
                                        <div className="flex-grow">
                                            <span className="text-sm text-white lg:hidden">
                                                {referral
                                                    ? referral.length > 35
                                                        ? referral.substring(0, 35) + "..."
                                                        : referral
                                                    : t("notAvailable")}
                                            </span>
                                            <span className="hidden text-sm text-white lg:block">
                                                {referral || t("notAvailable")}
                                            </span>
                                        </div>
                                        <img
                                            src="icons/copy_white.png"
                                            alt="Copy"
                                            className="size-sm"
                                        />
                                    </div>
                                    <button
                                        className="w-full flex-grow container-equal-sm gap-2 rounded-lg btn-gradient-border mb-5"
                                        onClick={() => referral && handleCopyId(referral)}
                                    >
                                        <span className="text-sm font-bold">
                                            {t("shareYourInviteCode")}
                                        </span>
                                    </button>
                                </>
                            )}

                            <div className="w-full">
                                <div className="flex items-start mb-3">
                                    <img
                                        src="icons/tick_icon.png"
                                        alt="Tick"
                                        className="size-xs mr-3 mt-[2px]"
                                    />
                                    <p className="text-white text-sm">{t("referralStep1")}</p>
                                </div>
                                <div className="flex items-start mb-3">
                                    <img
                                        src="icons/tick_icon.png"
                                        alt="Tick"
                                        className="size-xs mr-3 mt-[2px]"
                                    />
                                    <p className="text-white text-sm">{t("referralStep2")}</p>
                                </div>
                                <div className="flex items-start">
                                    <img
                                        src="icons/tick_icon.png"
                                        alt="Tick"
                                        className="size-xs mr-3 mt-[2px]"
                                    />
                                    <p className="text-white text-sm">{t("referralStep3")}</p>
                                </div>
                                <div className="flex items-start">
                                    <img
                                        src="icons/tick_icon.png"
                                        alt="Tick"
                                        className="size-xs mr-3 mt-[2px]"
                                    />
                                    <p className="text-white text-sm">{t("referralStep4")}</p>
                                </div>
                            </div>
                        </div>

                        <div
                            className={`rounded-lg bg-option-blue mb-4 overflow-x-auto ${
                                referralRebateList.length === 0 ? "rounded-b-none" : ""
                            }`}
                        >
                            <table className="w-full text-white text-sm">
                                <thead>
                                    <tr className="bg-dropdown-bg">
                                        <th className="p-3 text-left" colSpan="2">{t("referralCommissionRate")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {referralRebateList.length > 0 ? (
                                        referralRebateList.map((rebate, index) => (
                                            <tr
                                                key={index}
                                                className="border-b border-option-selected-blue last:border-b-0"
                                            >
                                                <td className={`p-3 ${index % 2 === 0 ? 'bg-odd-row' : 'bg-even-row'}`}>
                                                    {rebate.product_name}
                                                </td>
                                                <td className={`p-3 ${index % 2 === 0 ? 'bg-odd-row' : 'bg-even-row'}`}>
                                                    {Number(rebate.rate).toFixed(2)} %
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="2" className="empty-state">
                                                {t("noRebatesFound")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        
                        <div className="w-full flex justify-between items-center rounded-lg dropdown-gradient-border container-equal-sm text-white px-4 py-3 mb-4">
                            <div className="flex-1">
                                <span className="text-sm">{t("totalRewardEarn")}</span>
                                <span className="block text-2xl font-bold mt-1">{`${config.CURRENCY_ISO_CODE} ${formatCurrency(totalCommission, config.CURRENCY_ISO_CODE)}`}</span>
                            </div>
                            <div className="px-4">
                                <div className="w-px h-20 bg-white opacity-30"></div>
                            </div>
                            <div className="flex-1">
                                <span className="text-sm flex w-[50%]">{t("totalReferrals")}</span>
                                <span className="block text-2xl font-bold mt-1">{totalReferrals}</span>
                            </div>
                        </div>

                        <p className="mb-4 font-bold text-sm text-white">
                            {t("myReferrals")}
                        </p>

                        <div
                            className={`rounded-lg bg-option-blue mb-4 overflow-x-auto ${
                                referralList.length === 0 ? "rounded-b-none" : ""
                            }`}
                        >
                            <table className="w-full text-white text-sm">
                                <thead>
                                    <tr className="bg-dropdown-bg">
                                        <th className="p-3 text-left w-[40%]">
                                            {t("registerDate")}
                                        </th>
                                        <th className="p-3 text-left">
                                            {t("bonusCommission")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {referralList.length > 0 ? (
                                        referralList.map((referral, index) => (
                                            <tr
                                                key={index}
                                                className="border-b border-option-selected-blue last:border-b-0"
                                            >
                                                <td className={`p-3 ${index % 2 === 0 ? 'bg-odd-row' : 'bg-even-row'}`}>
                                                    {referral.date ? new Date(referral.date).toLocaleDateString('en-US') : "-"}
                                                </td>
                                                <td className={`p-3 ${index % 2 === 0 ? 'bg-odd-row' : 'bg-even-row'}`}>
                                                    {(Number(referral?.total_cashback) || 0).toFixed(2)}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan="4"
                                                className="empty-state"
                                            >
                                                {t("noReferralsFound")}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        </>
                    )}
                </div>
            </div>

            <FloatingMenu />
        </section>
    );
};

// === Export ===
export default Referral;